import { parseGid } from "@shopify/hydrogen-react";

import type { CartProduct, CartProductVariant } from "@tablescape/state";

// Full-document ProductHero (+ reviews) swap for cross-product size
// navigation (ProductBridge). This intentionally fetches and parses the
// destination's full HTML document rather than using Section Rendering,
// because Section Rendering only re-renders sections for the CURRENT
// product/template and cannot resolve a different product's sections
// across handles.
const PREFETCHED_URLS = new Set<string>();

/** Query params that must stay on document fetch/prefetch (preview / QA). */
const DOCUMENT_URL_PRESERVED_PARAMS = [
  "preview_theme_id",
  "isShopliftMerchant",
] as const;

type BuildSizeNavigationUrlParams = {
  currentHref?: string;
  product: Pick<CartProduct, "handle" | "onlineStoreUrl">;
  selectedVariant?: Pick<CartProductVariant, "id">;
};

/**
 * Builds the durable, indexable destination URL for a size/combo switch.
 * Preserves existing query params (e.g. preview_theme_id) except `variant`,
 * and resolves the destination on the CURRENT origin so local dev / preview
 * themes never escape to the production domain.
 */
export const buildSizeNavigationUrl = ({
  currentHref,
  product,
  selectedVariant,
}: BuildSizeNavigationUrlParams) => {
  const baseHref =
    currentHref ??
    (typeof window !== "undefined"
      ? window.location.href
      : "https://fromourplace.com");
  const currentUrl = new URL(baseHref);
  const productPath = product.onlineStoreUrl
    ? new URL(product.onlineStoreUrl, currentUrl.origin).pathname
    : `/products/${product.handle}`;
  const destinationUrl = new URL(productPath, currentUrl.origin);

  currentUrl.searchParams.forEach((value, key) => {
    if (key !== "variant" && !destinationUrl.searchParams.has(key)) {
      destinationUrl.searchParams.set(key, value);
    }
  });

  if (selectedVariant) {
    destinationUrl.searchParams.set("variant", parseGid(selectedVariant.id).id);
  }

  return destinationUrl.toString();
};

type BuildSizeNavigationDocumentUrlOptions = {
  preserveVariant?: boolean;
};

/**
 * Document URL used for prefetch/fetch. Prefetch drops `variant` and other
 * ephemeral params, but keeps preview/QA params so soft-load HTML matches the
 * theme the address bar is previewing. Navigation/popstate fetches pass
 * `{ preserveVariant: true }` so Back restores the selected variant.
 */
export const buildSizeNavigationDocumentUrl = (
  destinationUrl: string,
  options: BuildSizeNavigationDocumentUrlOptions = {}
) => {
  const url = new URL(destinationUrl);
  const preserved = new URLSearchParams();
  const keys = options.preserveVariant
    ? [...DOCUMENT_URL_PRESERVED_PARAMS, "variant"]
    : DOCUMENT_URL_PRESERVED_PARAMS;

  for (const key of keys) {
    const value = url.searchParams.get(key);
    if (value != null) {
      preserved.set(key, value);
    }
  }

  url.search = preserved.toString();
  return url.toString();
};

/**
 * QA/debug override: forces a normal hard navigation instead of soft-load.
 * Soft-load itself is opt-in per ProductBridge via `soft_load`.
 */
export const isSizeNavigationHardOverrideEnabled = (
  search: string | URLSearchParams
) => {
  const params =
    typeof search === "string" ? new URLSearchParams(search) : search;

  return params.get("size_nav") === "hard";
};

export const isSizeNavigationHardOverrideEnabledFromLocation = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return isSizeNavigationHardOverrideEnabled(window.location.search);
};

/**
 * True when this click should soft-load (primary click, no modifiers).
 * Modifier / non-primary clicks keep native link behavior (new tab, etc.).
 */
export const shouldInterceptSizeNavigationClick = (event: {
  altKey: boolean;
  button: number;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
}) => {
  if (event.button !== 0) {
    return false;
  }

  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return false;
  }

  return true;
};

const PRODUCT_REVIEWS_COMPONENT_SELECTOR =
  '[data-tbsp-component="ProductReviews"]';
const YOTPO_WIDGET_SELECTOR = ".yotpo-widget-instance";
const SIZE_NAVIGATION_HEAD_METADATA_SELECTOR = [
  'link[rel="canonical"]',
  'link[rel="alternate"][hreflang]',
  'meta[name="description"]',
  'meta[property^="og:"]',
  'meta[name^="twitter:"]',
  'script[type="application/ld+json"]',
].join(",");

// The Yotpo app embed injects an empty widget instance directly under <body>
// at runtime. Scope lookups to section-hosted instances so the injected one
// cannot shadow the reviews widget section.
//
// Assumption: PDP templates render a single section-hosted widget instance
// (the tbsp-yotpo-widget reviews section). A Yotpo app block inside an
// earlier section would shadow it — anchor on the reviews widget specifically
// if that becomes a real template pattern.
const SECTION_SCOPED_YOTPO_WIDGET_SELECTOR = `[id^="shopify-section-"] ${YOTPO_WIDGET_SELECTOR}`;

const getProductHeroSection = () => {
  if (typeof document === "undefined") {
    return undefined;
  }

  const productHero = document.querySelector("#tbsp-product-hero-snippet");

  return (
    productHero?.closest<HTMLElement>("[id^='shopify-section-']") ?? undefined
  );
};

const getProductHeroSnippet = (root: Document | Element) =>
  root.querySelector<HTMLScriptElement>(
    "#tbsp-product-hero-snippet [data-snippet]"
  ) ??
  root.querySelector<HTMLScriptElement>(
    '[id^="tbsp-product"][id$="-snippet"] [data-snippet]'
  );

const getProductHeroSectionFromDocument = (destinationDocument: Document) => {
  const snippet = getProductHeroSnippet(destinationDocument);

  return snippet?.closest<HTMLElement>('[id^="shopify-section-"]');
};

const getProductReviewsSection = () => {
  if (typeof document === "undefined") {
    return undefined;
  }

  const productReviews = document.querySelector(
    PRODUCT_REVIEWS_COMPONENT_SELECTOR
  );

  return (
    productReviews?.closest<HTMLElement>("[id^='shopify-section-']") ??
    undefined
  );
};

const getProductReviewsSectionFromDocument = (
  destinationDocument: Document
) => {
  const productReviews = destinationDocument.querySelector(
    PRODUCT_REVIEWS_COMPONENT_SELECTOR
  );

  return productReviews?.closest<HTMLElement>('[id^="shopify-section-"]');
};

const getYotpoWidgetSection = () => {
  if (typeof document === "undefined") {
    return undefined;
  }

  const yotpoWidget = document.querySelector(
    SECTION_SCOPED_YOTPO_WIDGET_SELECTOR
  );

  return (
    yotpoWidget?.closest<HTMLElement>("[id^='shopify-section-']") ?? undefined
  );
};

const getYotpoWidgetSectionFromDocument = (destinationDocument: Document) => {
  const yotpoWidget = destinationDocument.querySelector(
    SECTION_SCOPED_YOTPO_WIDGET_SELECTOR
  );

  return yotpoWidget?.closest<HTMLElement>('[id^="shopify-section-"]');
};

const refreshYotpoWidgets = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.yotpo?.refreshWidgets();
};

// Each section type validates its own hydration payload before a swap, so a
// destination section missing the payload it needs fails closed to the hard
// fallback instead of swapping in un-hydratable markup.
const heroSectionHasPayload = (section: HTMLElement) =>
  section.querySelector("[data-snippet]") != null;

const productReviewsSectionHasPayload = (section: HTMLElement) =>
  section.querySelector("script[data-section-id]") != null;

const yotpoWidgetSectionHasPayload = (section: HTMLElement) =>
  section.querySelector(YOTPO_WIDGET_SELECTOR) != null;

const prefetchUrl = (url: string) => {
  if (PREFETCHED_URLS.has(url)) {
    return;
  }

  PREFETCHED_URLS.add(url);

  fetch(url, {
    cache: "force-cache",
    credentials: "same-origin",
  }).catch(() => undefined);
};

export const prefetchSizeNavigationUrl = (destinationUrl: string) => {
  if (typeof window === "undefined" || typeof fetch === "undefined") {
    return;
  }

  prefetchUrl(buildSizeNavigationDocumentUrl(destinationUrl));
};

export const scheduleSizeNavigationPrefetch = (destinationUrl: string) => {
  if (typeof window === "undefined") {
    return;
  }

  const runPrefetch = () => prefetchSizeNavigationUrl(destinationUrl);

  if (window.requestIdleCallback) {
    window.requestIdleCallback(runPrefetch);
    return;
  }

  window.setTimeout(runPrefetch, 250);
};

const setProductHeroSoftLoading = (isLoading: boolean) => {
  const section = getProductHeroSection();

  if (!section) {
    return;
  }

  section.toggleAttribute("aria-busy", isLoading);

  section
    .querySelectorAll<HTMLElement>("a, button, input, select, textarea")
    .forEach((element) => {
      if (isLoading) {
        element.setAttribute("data-size-nav-soft-disabled", "true");

        if (
          element instanceof HTMLButtonElement ||
          element instanceof HTMLInputElement ||
          element instanceof HTMLSelectElement ||
          element instanceof HTMLTextAreaElement
        ) {
          element.setAttribute(
            "data-size-nav-was-disabled",
            element.disabled ? "true" : "false"
          );
          element.disabled = true;
          return;
        }

        element.setAttribute("aria-disabled", "true");
        element.style.pointerEvents = "none";
        return;
      }

      if (!element.hasAttribute("data-size-nav-soft-disabled")) {
        return;
      }

      element.removeAttribute("data-size-nav-soft-disabled");

      if (
        element instanceof HTMLButtonElement ||
        element instanceof HTMLInputElement ||
        element instanceof HTMLSelectElement ||
        element instanceof HTMLTextAreaElement
      ) {
        const wasDisabled =
          element.getAttribute("data-size-nav-was-disabled") === "true";
        element.removeAttribute("data-size-nav-was-disabled");
        element.disabled = wasDisabled;
        return;
      }

      element.removeAttribute("aria-disabled");
      element.style.pointerEvents = "";
    });
};

const parseShopifySectionHtml = (sectionHtml: string) => {
  if (typeof DOMParser === "undefined") {
    return undefined;
  }

  const parsedDocument = new DOMParser().parseFromString(
    sectionHtml,
    "text/html"
  );

  return (
    parsedDocument.querySelector<HTMLElement>('[id^="shopify-section-"]') ??
    undefined
  );
};

type SoftLoadDestinationSections = {
  headMetadata: HTMLElement[];
  productHeroSectionHtml: string;
  productReviewsSectionHtml?: string;
  title?: string;
  yotpoWidgetSectionHtml?: string;
};

type SoftLoadPlan = {
  headMetadata: HTMLElement[];
  heroSection: HTMLElement;
  reviewsSection?: HTMLElement;
  title?: string;
  yotpoSection?: HTMLElement;
};

/**
 * Build a fully validated swap plan before any live DOM mutation so we never
 * leave a destination hero with source reviews (torn UI).
 */
const buildSoftLoadPlan = (
  destination: SoftLoadDestinationSections
): SoftLoadPlan | undefined => {
  const heroSection = parseShopifySectionHtml(
    destination.productHeroSectionHtml
  );
  if (!heroSection || !heroSectionHasPayload(heroSection)) {
    return undefined;
  }

  const currentHasReviews = Boolean(getProductReviewsSection());
  const currentHasYotpo = Boolean(getYotpoWidgetSection());

  let reviewsSection: HTMLElement | undefined;
  if (currentHasReviews) {
    if (!destination.productReviewsSectionHtml) {
      return undefined;
    }

    reviewsSection = parseShopifySectionHtml(
      destination.productReviewsSectionHtml
    );
    if (!reviewsSection || !productReviewsSectionHasPayload(reviewsSection)) {
      return undefined;
    }
  }

  let yotpoSection: HTMLElement | undefined;
  if (currentHasYotpo) {
    if (!destination.yotpoWidgetSectionHtml) {
      return undefined;
    }

    yotpoSection = parseShopifySectionHtml(destination.yotpoWidgetSectionHtml);
    if (!yotpoSection || !yotpoWidgetSectionHasPayload(yotpoSection)) {
      return undefined;
    }
  }

  return {
    headMetadata: destination.headMetadata,
    heroSection,
    reviewsSection,
    title: destination.title,
    yotpoSection,
  };
};

const replaceHeadMetadata = (headMetadata: HTMLElement[]) => {
  if (headMetadata.length === 0) {
    return;
  }

  document.head
    .querySelectorAll(SIZE_NAVIGATION_HEAD_METADATA_SELECTOR)
    .forEach((element) => element.remove());
  document.head.append(...headMetadata);
};

const applySoftLoadPlan = (plan: SoftLoadPlan) => {
  // Resolve every live target before mutating so we never commit a partial swap.
  const currentHero = getProductHeroSection();
  const currentReviews = plan.reviewsSection
    ? getProductReviewsSection()
    : undefined;
  const currentYotpo = plan.yotpoSection ? getYotpoWidgetSection() : undefined;

  if (!currentHero) {
    return undefined;
  }

  if (plan.reviewsSection && !currentReviews) {
    return undefined;
  }

  if (plan.yotpoSection && !currentYotpo) {
    return undefined;
  }

  currentHero.replaceWith(plan.heroSection);
  const heroSectionId = plan.heroSection.id.replace("shopify-section-", "");

  let reviewsSectionId: string | undefined;
  if (plan.reviewsSection && currentReviews) {
    currentReviews.replaceWith(plan.reviewsSection);
    reviewsSectionId = plan.reviewsSection.id.replace("shopify-section-", "");
  }

  let yotpoSectionId: string | undefined;
  if (plan.yotpoSection && currentYotpo) {
    currentYotpo.replaceWith(plan.yotpoSection);
    yotpoSectionId = plan.yotpoSection.id.replace("shopify-section-", "");
  }

  replaceHeadMetadata(plan.headMetadata);

  return {
    heroSectionId,
    reviewsSectionId,
    yotpoSectionId,
  };
};

const fetchSoftLoadDestinationSections = async (
  destinationUrl: string,
  signal?: AbortSignal
): Promise<SoftLoadDestinationSections | undefined> => {
  const productDocumentUrl = buildSizeNavigationDocumentUrl(destinationUrl, {
    preserveVariant: true,
  });

  const documentResponse = await fetch(productDocumentUrl, {
    credentials: "same-origin",
    signal,
  });

  if (!documentResponse.ok) {
    return undefined;
  }

  const destinationDocument = new DOMParser().parseFromString(
    await documentResponse.text(),
    "text/html"
  );
  const productHeroSection =
    getProductHeroSectionFromDocument(destinationDocument);
  const productHeroSectionHtml = productHeroSection?.outerHTML;

  if (!productHeroSectionHtml) {
    return undefined;
  }

  const productReviewsSection =
    getProductReviewsSectionFromDocument(destinationDocument);
  const yotpoWidgetSection =
    getYotpoWidgetSectionFromDocument(destinationDocument);
  // `DOMParser` closes `<head>` earlier than the browser's own parser, so some
  // page-level metadata (notably the hreflang alternates) lands in `body` and
  // a head-scoped lookup drops it. Recover only direct children of `body` —
  // anything deeper is owned by a section (e.g. a section's own JSON-LD) and
  // must stay there rather than be duplicated into `head`.
  const headMetadata = [
    ...destinationDocument.head.querySelectorAll<HTMLElement>(
      SIZE_NAVIGATION_HEAD_METADATA_SELECTOR
    ),
    ...Array.from(
      destinationDocument.body.querySelectorAll<HTMLElement>(
        SIZE_NAVIGATION_HEAD_METADATA_SELECTOR
      )
    ).filter((element) => element.parentElement === destinationDocument.body),
  ];

  return {
    headMetadata,
    productHeroSectionHtml,
    productReviewsSectionHtml: productReviewsSection?.outerHTML,
    title: destinationDocument.title.trim() || undefined,
    yotpoWidgetSectionHtml: yotpoWidgetSection?.outerHTML,
  };
};

const dispatchSectionLoad = (sectionId: string) => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent("shopify:section:load", {
      detail: {
        sectionId,
      },
    })
  );
};

const SIZE_NAVIGATION_LIVE_REGION_ID = "size-navigation-live";

const announceSizeNavigationChange = (message: string) => {
  if (typeof document === "undefined" || message.length === 0) {
    return;
  }

  let region = document.getElementById(SIZE_NAVIGATION_LIVE_REGION_ID);
  if (!region) {
    region = document.createElement("div");
    region.id = SIZE_NAVIGATION_LIVE_REGION_ID;
    region.setAttribute("aria-live", "polite");
    region.setAttribute("aria-atomic", "true");
    region.style.cssText =
      "position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0)";
    document.body.appendChild(region);
  }

  region.textContent = "";
  region.textContent = message;
};

const findSelectedSizeLink = (hero: HTMLElement, destinationPath: string) => {
  for (const link of hero.querySelectorAll<HTMLAnchorElement>("a[href]")) {
    if (link.closest("noscript")) {
      continue;
    }

    try {
      if (
        new URL(link.href, window.location.origin).pathname === destinationPath
      ) {
        return link;
      }
    } catch {
      continue;
    }
  }

  return undefined;
};

const restoreSizeNavigationFocus = (destinationUrl: string) => {
  const hero = getProductHeroSection();
  if (!hero) {
    return;
  }

  const destinationPath = new URL(destinationUrl, window.location.origin)
    .pathname;
  const target =
    findSelectedSizeLink(hero, destinationPath) ??
    hero.querySelector<HTMLElement>("#tbsp-product-hero-snippet h1") ??
    hero.querySelector<HTMLElement>("#tbsp-product-hero-snippet");

  if (!target) {
    return;
  }

  if (
    !(target instanceof HTMLAnchorElement) &&
    !target.hasAttribute("tabindex")
  ) {
    target.tabIndex = -1;
  }

  target.focus();
};

type SoftLoadSizeNavigationOptions = {
  /**
   * `push` (default): click-driven soft switch.
   * `none`: history already matches (e.g. popstate) — do not push again.
   */
  historyMode?: "push" | "none";
};

let softLoadGeneration = 0;
let softLoadAbortController: AbortController | undefined;
let softLoadInFlight = false;

const SIZE_NAVIGATION_HISTORY_KEY = "sizeNavigation";
const SIZE_NAVIGATION_HISTORY_STATE = {
  [SIZE_NAVIGATION_HISTORY_KEY]: true,
} as const;

export const isSizeNavigationHistoryState = (state: unknown) => {
  if (typeof state !== "object" || state === null) {
    return false;
  }

  return (
    SIZE_NAVIGATION_HISTORY_KEY in state &&
    state[SIZE_NAVIGATION_HISTORY_KEY] === true
  );
};

const pushMarkedSizeNavigationHistory = (destinationUrl: string) => {
  if (!isSizeNavigationHistoryState(window.history.state)) {
    window.history.replaceState(
      SIZE_NAVIGATION_HISTORY_STATE,
      "",
      window.location.href
    );
  }

  window.history.pushState(SIZE_NAVIGATION_HISTORY_STATE, "", destinationUrl);
};

const scheduleSizeNavigationFocusRestore = (
  generation: number,
  destinationUrl: string
) => {
  const restoreFocus = () => {
    if (generation !== softLoadGeneration) {
      return;
    }

    restoreSizeNavigationFocus(destinationUrl);
  };

  if (typeof requestAnimationFrame === "function") {
    requestAnimationFrame(restoreFocus);
    return;
  }

  window.setTimeout(restoreFocus, 0);
};

/** True while a soft-load fetch/apply is in progress (latest-wins generation). */
export const isSizeNavigationSoftLoadInFlight = () => softLoadInFlight;

/**
 * Soft-load popstate owns React ProductHero size history. Legacy
 * `ProductURL` popstate should no-op when this is true.
 */
export const doesSizeNavigationOwnPopstate = () =>
  typeof window !== "undefined" &&
  isSizeNavigationHistoryState(window.history.state);

/**
 * Soft-loads a size destination in place: fetches the destination's full
 * document, validates hero + required reviews/Yotpo, then swaps atomically,
 * synchronizes SEO/social head metadata, updates history (unless
 * `historyMode: "none"`), updates `document.title`, and dispatches
 * `shopify:section:load`. Returns false (fails closed) on failure so the
 * caller can hard-navigate. Overlapping calls use latest-wins; superseded
 * calls return true so callers do not hard-navigate.
 */
export const softLoadSizeNavigationUrl = async (
  destinationUrl: string,
  options: SoftLoadSizeNavigationOptions = {}
) => {
  const historyMode = options.historyMode ?? "push";

  if (
    typeof window === "undefined" ||
    typeof fetch === "undefined" ||
    typeof history === "undefined" ||
    typeof DOMParser === "undefined"
  ) {
    return false;
  }

  if (!getProductHeroSection()) {
    return false;
  }

  softLoadAbortController?.abort();
  softLoadAbortController = new AbortController();
  const { signal } = softLoadAbortController;
  const generation = ++softLoadGeneration;
  softLoadInFlight = true;

  setProductHeroSoftLoading(true);

  try {
    const destinationSections = await fetchSoftLoadDestinationSections(
      destinationUrl,
      signal
    );

    if (generation !== softLoadGeneration) {
      return true;
    }

    if (!destinationSections) {
      softLoadInFlight = false;
      setProductHeroSoftLoading(false);
      return false;
    }

    const plan = buildSoftLoadPlan(destinationSections);
    if (!plan) {
      softLoadInFlight = false;
      setProductHeroSoftLoading(false);
      return false;
    }

    if (generation !== softLoadGeneration) {
      return true;
    }

    const applied = applySoftLoadPlan(plan);
    if (!applied) {
      softLoadInFlight = false;
      setProductHeroSoftLoading(false);
      return false;
    }

    if (historyMode === "push") {
      pushMarkedSizeNavigationHistory(destinationUrl);
    }

    if (plan.title) {
      document.title = plan.title;
    }

    dispatchSectionLoad(applied.heroSectionId);

    if (applied.reviewsSectionId) {
      dispatchSectionLoad(applied.reviewsSectionId);
    }

    if (applied.yotpoSectionId) {
      refreshYotpoWidgets();
    }

    announceSizeNavigationChange(plan.title ?? document.title);
    scheduleSizeNavigationFocusRestore(generation, destinationUrl);

    if (generation === softLoadGeneration) {
      softLoadInFlight = false;
    }

    return true;
  } catch {
    if (signal.aborted || generation !== softLoadGeneration) {
      return true;
    }

    softLoadInFlight = false;
    setProductHeroSoftLoading(false);
    return false;
  }
};

/**
 * Fail-closed hard-navigation fallback. Navigates directly to the
 * destination URL — no `size_nav=hard` treatment param is appended, since
 * that flag is a QA/debug override only and must never be required (or
 * forced) for customers.
 */
export const navigateToHardSizeNavigationFallback = (
  destinationUrl: string
) => {
  if (typeof window === "undefined") {
    return;
  }

  window.location.assign(destinationUrl);
};

let sizeNavigationPopstateBound = false;

/**
 * Bind once: on browser back/forward, re-apply soft-load for the history
 * URL. Fail closed to hard navigation if soft-load cannot apply.
 */
export const ensureSizeNavigationPopstateListener = () => {
  if (typeof window === "undefined" || sizeNavigationPopstateBound) {
    return;
  }

  sizeNavigationPopstateBound = true;

  window.addEventListener("popstate", (event) => {
    if (!isSizeNavigationHistoryState(event.state)) {
      return;
    }

    void softLoadSizeNavigationUrl(window.location.href, {
      historyMode: "none",
    }).then((didSoftLoad) => {
      if (!didSoftLoad) {
        navigateToHardSizeNavigationFallback(window.location.href);
      }
    });
  });
};
